import React from 'react';
import { applyTypography, formatDateToDDMMYY, useT } from '@repo/common';

import StarRating from './StarRating';

/**
 * 워크숍 후기 목록.
 *
 * <p>작성은 마이페이지(참여한 워크숍)에서 한다. 여기서는 읽기만 한다 —
 * 상세 페이지는 아직 예약 안 한 사람이 더 많이 보는 곳이라 작성 UI 를 두면 혼란스럽다.
 */
const ReviewsSection = ({ summary, loading = false }) => {
  const t = useT();
  const reviews = summary?.reviews ?? [];

  if (loading) {
    return (
      <div className='flex w-full flex-col gap-3'>
        {[0, 1].map((i) => (
          <div
            key={i}
            className='h-[92px] w-full animate-pulse rounded-xl bg-grayscale-gray02'
          />
        ))}
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className='flex w-full flex-col items-center gap-2 rounded-xl border border-dashed border-grayscale-gray03 px-6 py-14 text-center'>
        <p className={`${applyTypography('b16Strong')} text-text-primary`}>
          {t('No reviews yet', '아직 후기가 없습니다')}
        </p>
        <p className={`${applyTypography('b14')} text-text-placeholder`}>
          {t(
            'Reviews appear here after guests attend the workshop.',
            '워크숍에 참여한 분들의 후기가 이곳에 올라옵니다.',
          )}
        </p>
      </div>
    );
  }

  return (
    <div className='flex w-full flex-col gap-6'>
      {/* 요약 */}
      <div className='flex items-center gap-3'>
        <span className={`${applyTypography('title2')} text-text-primary`}>
          {Number(summary.averageRating ?? 0).toFixed(1)}
        </span>
        <StarRating value={summary.averageRating} size='m' />
        <span className={`${applyTypography('b14')} text-text-placeholder`}>
          {t(
            `${summary.reviewCount} review${summary.reviewCount === 1 ? '' : 's'}`,
            `후기 ${summary.reviewCount}개`,
          )}
        </span>
      </div>

      <ul className='flex w-full flex-col gap-4'>
        {reviews.map((review) => (
          <li
            key={review.reviewId}
            className='flex w-full flex-col gap-2 rounded-xl bg-grayscale-gray01 p-4'
          >
            <div className='flex items-center gap-2'>
              {review.profileUrl ? (
                <img
                  src={review.profileUrl}
                  alt=''
                  className='size-8 shrink-0 rounded-full object-cover'
                />
              ) : (
                <div className='size-8 shrink-0 rounded-full bg-grayscale-gray03' />
              )}
              <div className='min-w-0 flex-1'>
                <p
                  className={`${applyTypography('b14Strong')} truncate text-text-primary`}
                >
                  {review.userName}
                  {review.mine && (
                    <span className='ml-1 text-green-deep03'>
                      {t('(you)', '(나)')}
                    </span>
                  )}
                </p>
                <p
                  className={`${applyTypography('b12')} text-text-placeholder`}
                >
                  {formatDateToDDMMYY(review.createdAt)}
                </p>
              </div>
              <StarRating value={review.rating} size='s' />
            </div>
            <p
              className={`${applyTypography('b14')} whitespace-pre-wrap text-text-tertiary`}
            >
              {review.content}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ReviewsSection;
